import React, { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { t } from 'i18next';
import { ContentStatuses } from '@sparted/shared-library/business/types/content';
import { LanguageCode } from '@sparted/shared-library/business/types';

import { getCurrentUserRoles } from 'Services/role/roleService';

import UIconButton from 'Components/unit/UIconButton/UIconButton';
import SLanguageSelector from 'Components/structural/SLanguageSelector/SLanguageSelector';
import UButton from 'Components/unit/UButton/UButton';
import SOptionsMenu from 'Components/structural/SOptionsMenu/SOptionsMenu';
import withTooltip from 'Components/hoc/withTooltip/withTooltip';

import styles from './SContentEditionBar.style';
import { getAvailableLanguagesFromStatus, getButtonsState } from './SContentEditionBar.utils';

const UIconButtonWithTooltip = withTooltip(UIconButton);

export type SContentEditionBarOption = {
  id: number;
  text: string;
  onClick: () => void;
};

export type SContentEditionBarProps = {
  style?: CSSProperties;
  title: string;
  lastSavedDate: string;
  identifier: string;
  selectedLanguageCode: LanguageCode;
  onSave: () => void;
  onValidate: () => void;
  onUpdateValidation: () => void;
  onUnValidate: () => void;
  onAskValidation: () => void;
  onRequestTranslation: () => void;
  onSaveTranslation: () => void;
  onPreviewClick: () => void;
  onBack: () => void;
  onDeleteLanguage: (language: LanguageCode) => void;
  isFormDirty: boolean;
  contentStatuses: ContentStatuses;
  options: SContentEditionBarOption[];
  setLanguageCode: (language: LanguageCode) => void;
  archived?: boolean;
};

export const SContentEditionBar = ({
  style = {},
  title,
  lastSavedDate,
  identifier,
  selectedLanguageCode,
  onBack,
  onSave,
  onAskValidation,
  onUnValidate,
  onUpdateValidation,
  onValidate,
  onRequestTranslation,
  onSaveTranslation,
  onPreviewClick,
  onDeleteLanguage,
  isFormDirty,
  contentStatuses,
  setLanguageCode,
  options,
  archived,
}: SContentEditionBarProps) => {
  const roles = getCurrentUserRoles();
  const contentStatus = contentStatuses[selectedLanguageCode];

  const languageOptions = useMemo(
    () => getAvailableLanguagesFromStatus({ statuses: contentStatuses, setLanguage: setLanguageCode }),
    [contentStatuses, setLanguageCode],
  );

  const selectedLanguage = useMemo(
    () => languageOptions.find(({ languageCode }) => languageCode === selectedLanguageCode) || languageOptions[0],
    [selectedLanguageCode, languageOptions],
  );

  const hasValidatedLanguage = useMemo(
    () => Object.values(contentStatuses).some((status: string) => status === 'validated'),
    [contentStatuses],
  );

  const hasUnvalidatedLanguage = useMemo(
    () =>
      Object.keys(contentStatuses).length > 1 &&
      Object.values(contentStatuses).some((status: string) => status !== 'validated'),
    [contentStatuses],
  );

  const { saveButton, validateButton } = getButtonsState({
    disabled: archived,
    isFormDirty,
    contentStatus,
    roles,
    onSave,
    onAskValidation,
    onUnValidate,
    onUpdateValidation,
    onValidate,
    onSaveTranslation,
  });

  return (
    <div style={{ ...styles.wrapper, ...style }}>
      <div style={styles.bar}>
        <div style={styles.sideContainer}>
          <UIconButton dataTestContext="back" style={styles.arrow} icon="arrow-left" onClick={onBack} />
          <div style={styles.contentInfo}>
            <div style={styles.title}>{title}</div>
            <div style={styles.identifier}>{identifier}</div>
          </div>
          <div style={styles.languageWrapper}>
            <SLanguageSelector
              style={styles.languageSelector}
              selectedLanguage={selectedLanguage}
              languageOptions={languageOptions}
              onAction={onRequestTranslation}
              onDeleteLanguage={(language) => onDeleteLanguage(language)}
              addLanguageButtonTooltip={
                !archived && !hasValidatedLanguage
                  ? t('content_translation:content_edition_bar.add_translation_tooltip')
                  : ''
              }
              disabledAddLanguage={archived || !hasValidatedLanguage}
              warning={hasUnvalidatedLanguage}
            />
          </div>
        </div>
        <div style={styles.sideContainer}>
          <UIconButtonWithTooltip
            tooltipEnabled={!archived}
            tooltipMaxWidth={330}
            tooltipPosition="bottom"
            tooltipContent={t('content_translation:content_edition_bar.validate_translation_tooltip')}
            icon="eye"
            onClick={onPreviewClick}
            ghost
            disabled={archived}
          />
          <div style={styles.wrapperDivider}>
            <UButton dataTestContext="validate" style={styles.translationButton} {...validateButton} />
          </div>
          <div style={styles.lastSavedSection}>{lastSavedDate}</div>
          <UButton dataTestContext="save" type="accentuated" style={styles.saveButton} {...saveButton} />
          <SOptionsMenu style={styles.optionsMenu} options={options} />
        </div>
      </div>
    </div>
  );
};

export default SContentEditionBar;
